import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'search'
})
export class SearchPipe implements PipeTransform {


  transform(value: any, keys: string, term: string) {
    if (!term) {
      return value;
    }

    const searchMapping: Record<string, number> = {
      'ItemCode': 0,
      'Description': 1,
      'ShortDetail': 2,
      'LongDetail': 3,
      'LongDetail1': 4,
      'LongDetail2': 5
    };

    // Handle special mapped term
    if (term === 'special' || term === 'Special') {
      const CustomFilter = JSON.parse(sessionStorage.getItem('blackFridayProducts'));
      return CustomFilter;
    }

    // Helpers
    const normalize = (s: string): string =>
      (s || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // strip diacritics
        .replace(/[_\-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const tokenize = (s: string): string[] => normalize(s).split(/[^a-z0-9]+/).filter(Boolean);

    const levenshtein = (a: string, b: string): number => {
      if (a === b) return 0;
      const al = a.length, bl = b.length;
      if (al === 0) return bl;
      if (bl === 0) return al;
      const v0 = new Array(bl + 1);
      const v1 = new Array(bl + 1);
      for (let i = 0; i <= bl; i++) v0[i] = i;
      for (let i = 0; i < al; i++) {
        v1[0] = i + 1;
        for (let j = 0; j < bl; j++) {
          const cost = a[i] === b[j] ? 0 : 1;
          v1[j + 1] = Math.min(v1[j] + 1, v0[j + 1] + 1, v0[j] + cost);
        }
        for (let j = 0; j <= bl; j++) v0[j] = v1[j];
      }
      return v0[bl];
    };

    const synonyms: Record<string, string[]> = {
      'autoship': ['auto ship', 'subscription', 'subscribe', 'smart order', 'smartorder'],
      'subscription': ['subscribe', 'autoship', 'smart order', 'smartorder'],
      'kit': ['pack', 'bundle', 'set'],
      'pack': ['kit', 'bundle', 'set'],
      'greens': ['green', 'powergreens', 'power greens'],
      'protein': ['proteins', 'shake'],
      'ult': ['ultimate', 'ult pack', 'ultpack'],
      'lifestyle': ['life style']
    };

    const dedupe = <T,>(arr: T[]) => Array.from(new Set(arr));

    const toVariants = (q: string): string[] => {
      const base = normalize(q);
      const vars = [base];
      // Simple plural/suffix handling
      if (base.endsWith('ies')) vars.push(base.slice(0, -3) + 'y');
      if (base.endsWith('es')) vars.push(base.slice(0, -2));
      if (base.endsWith('s')) vars.push(base.slice(0, -1));
      // Remove spaces for compact matches
      vars.push(base.replace(/\s+/g, ''));
      // Add synonyms if available
      if (synonyms[base]) vars.push(...synonyms[base].map(normalize));
      return dedupe(vars).filter(Boolean);
    };

    const keyArray = keys.split(',').map(k => k.trim()).filter(Boolean);
    const results: any[] = [];

    if (Array.isArray(value) && value.length > 0) {
      for (const item of value) {
        let bestScore = -Infinity;
        let bestKey: string = null;

        for (const key of keyArray) {
          if (!Object.prototype.hasOwnProperty.call(item, key)) continue;
          const fieldRaw = String(item[key] ?? '');
          const field = normalize(fieldRaw);
          const tokens = tokenize(field);

          let fieldScore = -Infinity;
          const variants = toVariants(term);

          for (const q of variants) {
            if (!q) continue;
            // Direct contains gets highest priority
            if (field.includes(q)) {
              // earlier matches get slightly higher score
              const score = 100 - Math.min(field.indexOf(q), 50);
              if (score > fieldScore) fieldScore = score;
              continue;
            }
            // Fuzzy (token-based) match using Levenshtein distance
            let minDist = Infinity;
            for (const t of tokens) {
              const d = levenshtein(q, t);
              if (d < minDist) minDist = d;
            }
            const len = Math.max(q.length, 1);
            // Allowed distance threshold scales with query length
            const allowed = len >= 6 ? 2 : len >= 4 ? 1 : 0;
            if (minDist <= allowed) {
              const sim = 1 - (minDist / len);
              const score = 60 + Math.round(sim * 40); // 60..100
              if (score > fieldScore) fieldScore = score;
            }
          }

          if (fieldScore > bestScore) {
            bestScore = fieldScore;
            bestKey = key;
          }
        }

        if (bestScore > -Infinity) {
          results.push({ ...item, source: bestKey, _score: bestScore });
        }
      }

      const sorted = results.sort((a, b) => {
        const ma = searchMapping[a.source] ?? 999;
        const mb = searchMapping[b.source] ?? 999;
        if (ma !== mb) return ma - mb;
        return (b._score ?? 0) - (a._score ?? 0);
      });

      // Strip meta
      return sorted.map(({ _score, ...rest }) => rest);
    }

    return value;
  }
}
